<template>
  <div id="app">
    <div class="background-pattern"></div>

    <transition @enter="enter" @leave="leave" appear mode="in-out">
      <AppLoader v-if="!$store.state.loaded" data-transitionId="loader" />
      <GameFrame v-else /> <!-- Main component for game, contains HTML frame and canvas frame -->
    </transition>

    <RotateDevice />
  </div>
</template>

<script>
import { gsap } from 'gsap';
import { debounce } from 'lodash';
import { CustomEase } from '@/assets/libs/CustomEase';

import AppLoader from '@/components/AppLoader.vue';
import GameFrame from '@/components/GameFrame.vue';
import RotateDevice from '@/components/RotateDevice.vue';
import transition from '@/scripts/transitions';
import { isMobile } from './scripts/utils';

gsap.registerPlugin(CustomEase);
CustomEase.create('bloom', '.5,0,0,1');

export default {
  name: 'App',

  components: {
    AppLoader,
    GameFrame,
    RotateDevice,
  },

  mounted() {
    if (localStorage && parseInt(localStorage.getItem('GucciBloomTutorialDone'), 10) > 0) this.$store.commit('SET_TUTORIAL', 3);
    this.resize();
    window.addEventListener('resize', debounce(() => this.resize(), 200));
    window.addEventListener('blur', () => {
      if (this.$game && (this.$store.state.tutorial >= 3 || this.$store.state.tutorial <= -1)) this.$game.pause();
    });
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === 32 && this.$game && (this.$store.state.tutorial >= 3 || this.$store.state.tutorial <= -1)) {
        this.$game.togglePlayPlause();
      }
    });

    this.$audio.init();

    const loadingPromise = [];
    loadingPromise.push(this.$game.load());
    loadingPromise.push(this.$audio.load());
    if (this.$inApp && this.$isLogged) loadingPromise.push(this.$store.dispatch('getUserData'));

    Promise.all(loadingPromise).then((resources) => {
      console.log('%cAssets loaded', 'color: red');
      console.log('resources', resources);
      this.$store.commit('SET_LOADED', true);
    }).catch((error) => {
      console.log('error', error);
    });

    const sequence = [38, 40, 37, 39, 65, 66];
    const original = [38, 40, 37, 39, 65, 66];
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === sequence[0]) {
        sequence.splice(0, 1);
      } else {
        sequence.length = 0;
        original.forEach((n) => sequence.push(n));
      }
      if (sequence.length === 0) this.$eventHub.$emit('konami-code');
    });
  },

  methods: {
    resize() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      const h = window.innerHeight * 0.98;
      const rw = Math.max(h / 812, 1);
      document.documentElement.style.setProperty('--rw', `${rw}`);

      this.$eventHub.$emit('window-resize');
    },

    enter(el, done) {
      let transitionId = el.getAttribute('data-transitionId');
      let ctx = el;

      if (transitionId !== 'loader') {
        done();
        ctx = el.querySelector('[data-phase="intro"]');
        transitionId = ctx.dataset.phase;

        const bushesFrame = document.querySelector('.frame-bush');
        const stageFrame = document.querySelector('.stage-frame');
        const tl = gsap.timeline({ delay: 1.3 });

        gsap.set(stageFrame, { scale: 1.2 });
        if (!isMobile()) gsap.set(bushesFrame, { scale: 1.2, y: 40 });

        tl.addLabel('start');
        if (!isMobile()) {
          tl.to(bushesFrame, {
            scale: 1,
            y: 0,
            ease: 'bloom',
            duration: 1.2,
          }, 'start');
        }
        tl.to(stageFrame, {
          scale: 1,
          ease: 'bloom',
          duration: 1.2,
        }, 'start+=0.1');
        tl.add(() => { this.$eventHub.$emit('bush-in'); }, 'start+=0.6');
        tl.add(transition.get(`${transitionId}-enter`)(ctx), 'start+=0.7');
      } else {
        transition.get(`${transitionId}-enter`)(ctx).then(done);
      }
    },

    leave(el, done) {
      transition.get('loader-leave')(el).then(done);
    },
  },
};
</script>

<style lang="scss">
@import '@/scss/base.scss';
@import '@/scss/common.scss';

body {
  @include gradient-bg(1);
}

.background-pattern {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);

  pointer-events: none;

  background-image: url('/img/misc/repeater.png');
  background-size: calc(198 * var(--rw));
}
</style>
